/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { temperatureRepository } from '@/services/repositories/temperature.repository.js'

/**
 * Temperature Seeder
 * Creates dummy temperature history data for goats
 */

/**
 * Generate a random temperature in °C
 * @param {number} base - base temperature (normal goat body temp ~38.5-39.5°C)
 * @param {number} variance - +/- range around base
 * @returns {number}
 */
function randomTemperature(base = 39, variance = 1.5) {
  const min = base - variance
  const max = base + variance
  const value = min + Math.random() * (max - min)
  // Round to one decimal place
  return Math.round(value * 10) / 10
}

/**
 * Generate a random date in the past (last 90 days)
 * @returns {string} ISO date string
 */
function randomPastDate() {
  const now = Date.now()
  const daysAgo = Math.floor(Math.random() * 90) // 0-90 days ago
  const timestamp = now - (daysAgo * 24 * 60 * 60 * 1000)
  return new Date(timestamp).toISOString()
}

/**
 * Seed temperatures for a single goat
 * @param {number} goatId
 * @param {number} count - number of temperature records to create
 * @returns {Promise<number[]>} array of created temperature IDs (if available)
 */
async function seedTemperaturesForGoat(goatId, count = 5) {
  console.log(`  Seeding ${count} temperatures for goat ${goatId}...`)
  const createdIds = []

  for (let i = 0; i < count; i++) {
    const value = randomTemperature()
    
    const id = await temperatureRepository.create({
      goatId,
      value,
      measuredAt: randomPastDate()
    })
    createdIds.push(id)
    console.log(
      `    Created temperature: ${value} °C (id: ${id})`
    )
  }

  return createdIds
}

/**
 * Seed temperatures for a list of goats
 * @param {number[]} goatIds - array of goat IDs to create temperatures for
 * @param {number} temperaturesPerGoat - number of temperature records per goat
 * @returns {Promise<void>}
 */
export async function seedTemperatures(goatIds = [], temperaturesPerGoat = 5) {
  if (!goatIds.length) {
    console.log('️ No goat IDs provided for temperature seeding. Skipping temperatures.')
    return
  }

  console.log(
    `Seeding temperatures for ${goatIds.length} goats (about ${temperaturesPerGoat} per goat)...`
  )

  try {
    for (const goatId of goatIds) {
      await seedTemperaturesForGoat(goatId, temperaturesPerGoat)
    }

    console.log('Finished seeding temperatures.')
  } catch (error) {
    console.error('Failed to seed temperatures:', error)
    throw error
  }
}
